import { defineStore } from "pinia";
import { ref, type Ref } from "vue";
import { api } from "@/app/services/api.service";

export const useBillingStore = defineStore("billing", () => {
  // Service costs
  const serviceCosts: Ref<any> = ref(null);
  const serviceCost: Ref<any> = ref(null);
  const editServiceCostResult: Ref<any> = ref(null);
  const deleteServiceCostResult: Ref<any> = ref(null);

  // Document costs
  const documentCosts: Ref<any> = ref(null);
  const documentCost: Ref<any> = ref(null);
  const editDocumentCostResult: Ref<any> = ref(null);

  // Currencies
  const currencies: Ref<any> = ref(null);

  // Tax heads
  const taxHeads: Ref<any> = ref(null);
  const taxHead: Ref<any> = ref(null);

  // PRN management
  const usedPrns: Ref<any> = ref(null);
  const prnAdded: Ref<any> = ref(null);
  const prnRemoved: Ref<any> = ref(null);
  const prnError: Ref<any> = ref(null);

  // Transactions
  const transactions: Ref<any> = ref(null);
  const transactionSuccess: Ref<any> = ref(null);
  const transactionError: Ref<any> = ref(null);
  const prnReleaseSuccess: Ref<any> = ref(null);
  const prnReleaseError: Ref<any> = ref(null);

  // Shared error
  const error: Ref<any> = ref(null);

  // --- Service Costs ---
  const fetchServiceCosts = async () => {
    return api().get("configuration/service-charge/all")
      .then((response) => {
        serviceCosts.value = response.data;
      })
      .catch((err) => {
        error.value = err.response?.data;
      });
  };

  const createServiceCost = async (data: any) => {
    return api().post("configuration/service-charge/create", data)
      .then((response) => {
        serviceCost.value = response.data;
      })
      .catch((err) => {
        error.value = err.response?.data;
      });
  };

  const editServiceCost = async (data: any) => {
    return api().post("configuration/service-charge/update", data)
      .then((response) => {
        editServiceCostResult.value = response.data;
      })
      .catch((err) => {
        error.value = err.response?.data;
      });
  };

  const deleteServiceCost = async (serviceId: string | number) => {
    return api().get("configuration/service-charge/delete-by-id/?serviceChargeId=" + serviceId)
      .then((response) => {
        deleteServiceCostResult.value = response.data;
      })
      .catch((err) => {
        error.value = err.response?.data;
      });
  };

  // --- Document Costs ---
  const fetchDocumentCosts = async () => {
    return api().get("configuration/document-type/all")
      .then((response) => {
        documentCosts.value = response.data;
      })
      .catch((err) => {
        error.value = err.response?.data;
      });
  };

  const createDocumentCost = async (data: any) => {
    return api().post("configuration/document-type/create", data)
      .then((response) => {
        documentCost.value = response.data;
      })
      .catch((err) => {
        error.value = err.response?.data;
      });
  };

  const editDocumentCost = async (data: any) => {
    return api().post("configuration/document-type/update", data)
      .then((response) => {
        editDocumentCostResult.value = response.data;
      })
      .catch((err) => {
        error.value = err.response?.data;
      });
  };

  // --- Currencies ---
  const fetchCurrencies = async () => {
    return api().get("configuration/currency/all")
      .then((response) => {
        currencies.value = response.data;
      })
      .catch((err) => {
        error.value = err.response?.data;
      });
  };

  // --- Tax Heads ---
  const fetchTaxHeads = async () => {
    return api().get("configuration/tax-head/all")
      .then((response) => {
        taxHeads.value = response.data;
      })
      .catch((err) => {
        error.value = err.response?.data;
      });
  };

  const createTaxHead = async (data: any) => {
    return api().post("configuration/tax-head/create", data)
      .then((response) => {
        taxHead.value = response.data;
      })
      .catch((err) => {
        error.value = err.response?.data;
      });
  };

  const updateTaxHead = async (data: any) => {
    return api().post("configuration/tax-head/by-id?taxHeadId=" + data.id, data)
      .then((response) => {
        taxHead.value = response.data;
      })
      .catch((err) => {
        error.value = err.response?.data;
      });
  };

  // --- PRN Management ---
  const fetchUsedPRNs = async (data: { page: number; perPage: number }) => {
    return api().get(`billing/used-prn?page=${data.page}&perPage=${data.perPage}`)
      .then((response) => {
        usedPrns.value = response.data.data;
      })
      .catch((err) => {
        prnError.value = err.response?.data;
      });
  };

  const filterByPrn = async (data: { prn: string; page: number; perPage: number }) => {
    return api().get(`billing/used-prn/by-prn/${data.prn}?page=${data.page}&perPage=${data.perPage}`)
      .then((response) => {
        usedPrns.value = response.data.data;
      })
      .catch((err) => {
        prnError.value = err.response?.data;
      });
  };

  const addPRNs = async (data: any) => {
    return api().post("billing/used-prn/add", data)
      .then((response) => {
        prnAdded.value = response.data;
      })
      .catch((err) => {
        prnError.value = err.response?.data;
      });
  };

  const uploadPRNs = async (data: any) => {
    return api().post("billing/upload-used-prn-file", data)
      .then((response) => {
        prnAdded.value = response.data;
      })
      .catch((err) => {
        prnError.value = err.response?.data;
      });
  };

  const removePRN = async (prn: string | number) => {
    return api().get("billing/delete-prn/" + prn)
      .then((response) => {
        prnRemoved.value = response.data;
      })
      .catch((err) => {
        prnError.value = err.response?.data;
      });
  };

  // --- Transactions ---
  const fetchTransactions = async (data: any) => {
    let path = `/billing/transaction/list?page=${data.page}&perPage=${data.pageSize}&dir=desc`;
    if (data.trackingNumber) path += `&trackingNumber=${data.trackingNumber}`;
    if (data.prn) path += `&prn=${data.prn}`;
    if (data.status) path += `&status=${data.status}`;
    if (data.prnStatus) path += `&prnStatus=${data.prnStatus}`;
    if (data.paymentMethod) path += `&paymentMethod=${data.paymentMethod}`;
    return api().get(path)
      .then((response) => {
        transactions.value = response.data.data;
      })
      .catch((err) => {
        transactionError.value = err.response?.data;
      });
  };

  const retryTransactionVerification = async (data: any) => {
    return api().post("/billing/transaction/query", data)
      .then((response) => {
        transactionSuccess.value = response.data;
      })
      .catch((err) => {
        transactionError.value = err.response?.data;
      });
  };

  const releasePRNTransaction = async (data: any) => {
    return api().post("/billing/transaction/prn-release", data)
      .then((response) => {
        prnReleaseSuccess.value = response.data;
      })
      .catch((err) => {
        prnReleaseError.value = err.response?.data;
      });
  };

  const bulkSynchronizeTransactions = async (data: {
    startDate: string;
    endDate: string;
    transactionType: string;
  }) => {
    return api().post("/billing/transaction/bulk-synchronize", data);
  };

  return {
    serviceCosts, serviceCost, editServiceCostResult, deleteServiceCostResult,
    documentCosts, documentCost, editDocumentCostResult,
    currencies,
    taxHeads, taxHead,
    usedPrns, prnAdded, prnRemoved, prnError,
    transactions, transactionSuccess, transactionError, prnReleaseSuccess, prnReleaseError,
    error,
    fetchServiceCosts, createServiceCost, editServiceCost, deleteServiceCost,
    fetchDocumentCosts, createDocumentCost, editDocumentCost,
    fetchCurrencies,
    fetchTaxHeads, createTaxHead, updateTaxHead,
    fetchUsedPRNs, filterByPrn, addPRNs, uploadPRNs, removePRN,
    fetchTransactions, retryTransactionVerification, releasePRNTransaction,
    bulkSynchronizeTransactions,
  };
});
